import { LeftOutlined } from '@ant-design/icons'
import {
  Button,
  Checkbox,
  Drawer,
  Input,
  InputNumber,
  Select,
  Switch,
  message,
} from 'antd'
import { ReactMultiEmail, isEmail } from 'react-multi-email'
import 'react-multi-email/style.css'
import moment from 'moment'
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { Link, useLocation, useParams } from 'react-router-dom'
import styled from 'styled-components/macro'
import { BodyText, H3 } from 'src/branding/typography'
import {
  FoodSafetyHeaderRow,
  FoodSafetyHeroBand,
  FoodSafetyPageTitle,
  FoodSafetySurfaceCard,
} from 'src/components/food-safety/FoodSafetyPageChrome'
import FoodSafetyHoldingRangeCard from 'src/components/food-safety/FoodSafetyHoldingRangeCard'
import FoodSafetyCoolingTargetCard from 'src/components/food-safety/FoodSafetyCoolingTargetCard'
import FoodSafetyClockField from 'src/components/food-safety/FoodSafetyClockField'
import FoodSafetyOutOfServiceDialog from 'src/components/food-safety/FoodSafetyOutOfServiceDialog'
import FoodSafetyRangeHistory from 'src/components/food-safety/FoodSafetyRangeHistory'
import FoodSafetySetupReadinessCard from 'src/components/food-safety/FoodSafetySetupReadinessCard'
import FadeIn from 'src/components/styled/FadeIn'
import Loading from 'src/components/styled/Loading'
import OllieAskLink from 'src/components/ollie/OllieAskLink'
import PermissionBlockScreen from 'src/components/shared/PermissionBlockScreen'
import {
  FOOD_SAFETY_ADHOC_SECTION_META,
  FOOD_SAFETY_CALIBRATION_TOLERANCE_BOUNDS,
  FOOD_SAFETY_PROBE_REFERENCE_MAX_LENGTH,
  FOOD_SAFETY_DELIVERY_TEMP_BAND,
  FOOD_SAFETY_DELIVERY_TEMP_PRESETS,
  FOOD_SAFETY_FREQUENCY,
  FOOD_SAFETY_FREQUENCY_LABELS,
  FOOD_SAFETY_FRIDGE_MAX_DUE_TIMES,
  FOOD_SAFETY_HOLDING_SUGGESTED_RANGES,
  FOOD_SAFETY_OLLIE_COACH_DRAFT,
  FOOD_SAFETY_OLLIE_COACH_LABEL,
  FOOD_SAFETY_QUICK_PICK_HINT,
  FOOD_SAFETY_SCHEDULE_MODE,
  FOOD_SAFETY_SECONDARY_TEXT,
  FOOD_SAFETY_SECTION,
  FOOD_SAFETY_SECTION_LABELS,
  FOOD_SAFETY_TASK_KIND,
  FOOD_SAFETY_SURFACE,
  FOOD_SAFETY_WEEKDAY_OPTIONS,
  foodSafetyDrawerWidth,
} from 'src/constants/foodSafety'
import { fetchVenueProducts } from 'src/features/VenueProductsSlice'
import useFocusReturningDrawer from 'src/hooks/useFocusReturningDrawer'
import useFoodSafetyOutOfService from 'src/hooks/useFoodSafetyOutOfService'
import useFoodSafetyRecipes from 'src/hooks/useFoodSafetyRecipes'
import useFoodSafetySaveLock from 'src/hooks/useFoodSafetySaveLock'
import useIsBlocked, { useVenueCustomRoles } from 'src/hooks/useIsBlocked'
import useUserRole from 'src/hooks/useUserRole'
import {
  archiveFoodSafetyDefinition,
  createFoodSafetyDefinition,
  fetchFoodSafetyDefinitions,
  fetchFoodSafetySettings,
  updateFoodSafetyDefinition,
  updateFoodSafetySettings,
} from 'src/utils/foodSafetyApi'
import {
  canMarkFoodSafetyOutOfService,
  formatFoodSafetyOutOfServiceSince,
  getOpenFoodSafetyOutOfServicePeriod,
} from 'src/utils/foodSafetyOutOfService'
import {
  trackFoodSafetyOllieOpened,
  trackFoodSafetySetupSaved,
} from 'src/utils/foodSafetyAnalytics'
import {
  blankSetupForm,
  buildSetupPayload,
  foodSafetySetupVisibleSections,
  duplicateSetupForm,
  formFromDefinition,
  formatDefinitionSummary,
  formatSetupDeliveryBandChipRange,
  formatSetupDeliveryBandLabel,
  formatSetupDeliveryBandLivePreview,
  FOOD_SAFETY_SETUP_TEMPLATES_DEFERRED_HINT,
  foodSafetyManageBlockedCopy,
  FOOD_SAFETY_OPENING_DAY_OPTIONS,
  formatOpeningDaysClosedLine,
  formatOpeningDaysIntro,
  foodSafetyClosedDatesAfterAdd,
  foodSafetyClosedDatesAfterRemove,
  formatFoodSafetyClosedDate,
  readFoodSafetyClosedDates,
  foodSafetySetupFrequencyHint,
  openingWeekdaysEqual,
  resolveOpeningWeekdays,
  toggleOpeningWeekday,
  hasFoodSafetyFridgeDefinition,
  isCleaningJob,
  isFoodSafetyOptionalSetupSection,
  isFoodSafetySetupSection,
  resolveSetupScheduleMode,
  setupAddButtonLabel,
  setupProbeSchedulePatch,
  formatSetupNoScheduleHint,
  setupDrawerTitle,
  setupSaveButtonLabel,
  setupEmptyStateCopy,
  setupSaveSuccessMessage,
  validateSetupForm,
} from 'src/utils/foodSafetySetup'
import {
  addFridgeDueTime,
  formatFridgeFiveReadingsCopy,
  FOOD_SAFETY_FRIDGE_TWICE_DAILY_HINT,
  FOOD_SAFETY_FRIDGE_TWICE_DAILY_LABEL,
} from 'src/utils/foodSafetyFridges'
import { hasFoodSafetyRangeHistory } from 'src/utils/foodSafetyRangeHistory'
import { applyDeliveryTempPreset } from 'src/utils/foodSafetyAdHoc'
import {
  FOOD_SAFETY_PROBE_TEST_OPTIONS,
  formatProbeExpected,
  isDishwasherUnit,
  isProbeBothTests,
  probeTestPreset,
} from 'src/utils/foodSafetyProbe'
import {
  formatFoodSafetyHoldingRange,
  isFoodSafetyHoldingSection,
} from 'src/utils/foodSafetyHoldingRange'
import FoodSafetyShoppingListSuggestBlock from 'src/components/food-safety/FoodSafetyShoppingListSuggestBlock'
import { isFoodSafetyShoppingListSuggestSection } from 'src/utils/foodSafetyShoppingListSuggest'
import {
  FOOD_SAFETY_MANAGER_REPORT_EMAIL_MAX,
  canSaveFoodSafetyManagerEmails,
  mergeFoodSafetyManagerEmailPending,
  resolveFoodSafetyManagerReportEmails,
} from 'src/utils/foodSafetyManagerEmails'
import { confirmWithFocusReturn } from 'src/utils/dialogFocus'
import {
  FOOD_SAFETY_SAVED_REFRESH_FAILED_MESSAGE,
  refreshAfterFoodSafetySave,
} from 'src/utils/foodSafetySaveGuard'

const { Option } = Select

const findSetupAddButton = () =>
  document.querySelector('[data-testid="fs-setup-add"]')

const FoodSafetySetupScreen = () => {
  const blocked = useIsBlocked('food-safety-manage')
  const userRole = useUserRole()
  const customRoles = useVenueCustomRoles()
  const dispatch = useDispatch()
  const location = useLocation()
  const { venueId: routeVenueId } = useParams()
  const venue = useSelector(state => state.venue?.venue)
  const venueProducts = useSelector(
    state => state.venueProducts?.products || [],
  )
  const venueId = String(venue?._id || routeVenueId || '')

  const querySection = useMemo(() => {
    const raw = new URLSearchParams(location.search).get('section')
    return isFoodSafetySetupSection(raw) ? raw : null
  }, [location.search])

  const openAddOnLoad = useMemo(
    () => new URLSearchParams(location.search).get('add') === '1',
    [location.search],
  )

  const [sectionFilter, setSectionFilter] = useState(
    () => querySection || FOOD_SAFETY_SECTION.FRIDGES_FREEZERS,
  )
  const [definitions, setDefinitions] = useState([])
  const [settings, setSettings] = useState({
    managerReportEmail: '',
    managerReportEmails: [],
  })
  const [emailDrafts, setEmailDrafts] = useState([])
  const [pendingEmailInput, setPendingEmailInput] = useState('')
  const [loading, setLoading] = useState(true)
  const [loadedOnce, setLoadedOnce] = useState(false)
  const [savingEmail, setSavingEmail] = useState(false)
  const [openingDraft, setOpeningDraft] = useState(() =>
    resolveOpeningWeekdays(null),
  )
  const [savingOpeningDays, setSavingOpeningDays] = useState(false)
  const [savingTwiceDaily, setSavingTwiceDaily] = useState(false)
  const [closedDateDraft, setClosedDateDraft] = useState('')
  const [savingClosedDate, setSavingClosedDate] = useState(false)
  const drawer = useFocusReturningDrawer({ fallbackFocus: findSetupAddButton })
  const [editing, setEditing] = useState(null)
  const [form, setForm] = useState(() =>
    blankSetupForm(querySection || FOOD_SAFETY_SECTION.FRIDGES_FREEZERS),
  )
  const [savingForm, setSavingForm] = useState(false)
  const saveLock = useFoodSafetySaveLock()
  const recipes = useFoodSafetyRecipes(
    venueId,
    sectionFilter === FOOD_SAFETY_SECTION.COOLING ||
      form.section === FOOD_SAFETY_SECTION.COOLING,
  )

  const fetchSetup = useCallback(async () => {
    const [defs, settingsData] = await Promise.all([
      fetchFoodSafetyDefinitions(venueId, { includeInactive: false }),
      fetchFoodSafetySettings(venueId),
    ])
    setDefinitions(defs)
    const nextSettings = settingsData || {
      managerReportEmail: '',
      managerReportEmails: [],
    }
    setSettings(nextSettings)
    setEmailDrafts(resolveFoodSafetyManagerReportEmails(nextSettings))
    setOpeningDraft(resolveOpeningWeekdays(nextSettings))
  }, [venueId])

  const load = useCallback(async () => {
    if (!venueId) return
    setLoading(true)
    try {
      await fetchSetup()
    } catch (error) {
      console.error(error)
      message.error(error?.message || 'Could not load Food Safety setup.')
    } finally {
      setLoading(false)
      setLoadedOnce(true)
    }
  }, [venueId, fetchSetup])

  /**
   * After a save: reload in place (no spinner, so the drawer can close and
   * hand focus back). A failed reload reads "Saved — couldn't refresh".
   */
  const refreshAfterSave = async () => {
    if (!venueId) return
    if (!(await refreshAfterFoodSafetySave(fetchSetup))) {
      message.warning(FOOD_SAFETY_SAVED_REFRESH_FAILED_MESSAGE)
    }
  }

  useEffect(() => {
    load()
  }, [load])

  useEffect(() => {
    if (!venueId) return undefined
    dispatch(fetchVenueProducts(venueId))
    return undefined
  }, [venueId, dispatch])

  useEffect(() => {
    if (querySection && isFoodSafetySetupSection(querySection)) {
      setSectionFilter(querySection)
    }
  }, [querySection])

  const visibleSections = useMemo(
    () => foodSafetySetupVisibleSections(definitions),
    [definitions],
  )
  const hasFridge = hasFoodSafetyFridgeDefinition(definitions)
  const deferAdHocTemplates = !hasFridge

  // Before the first load the list is empty, which would hide every section
  // but Fridges and throw away a `?section=` link.
  useEffect(() => {
    if (!loadedOnce) return
    if (!visibleSections.includes(sectionFilter)) {
      setSectionFilter(FOOD_SAFETY_SECTION.FRIDGES_FREEZERS)
    }
  }, [loadedOnce, visibleSections, sectionFilter])

  const didAutoOpenAdd = useRef(false)
  useEffect(() => {
    if (!openAddOnLoad || loading || didAutoOpenAdd.current) return undefined
    didAutoOpenAdd.current = true
    const requested = querySection || sectionFilter
    const section = visibleSections.includes(requested)
      ? requested
      : FOOD_SAFETY_SECTION.FRIDGES_FREEZERS
    setSectionFilter(section)
    setEditing(null)
    setForm(blankSetupForm(section))
    drawer.show()
    return undefined
  }, [openAddOnLoad, loading, querySection, sectionFilter, visibleSections])

  // New identity only when setup reloads, so the readiness card refetches.
  const readinessRefreshKey = useMemo(
    () => ({ definitions, settings }),
    [definitions, settings],
  )
  const emailsCardRef = useRef(null)
  const emailFieldRef = useRef(null)
  // react-multi-email doesn't pass props to its input, so name it here.
  useEffect(() => {
    const input = emailFieldRef.current?.querySelector('input')
    if (input && !input.hasAttribute('aria-labelledby')) {
      input.setAttribute('aria-labelledby', 'fs-manager-emails-title')
    }
  })
  const sectionTabsRef = useRef(null)
  const scrollToSetupRef = ref => {
    const el = ref.current
    if (el && typeof el.scrollIntoView === 'function') {
      el.scrollIntoView({ behavior: 'smooth', block: 'start' })
    }
  }

  const filtered = useMemo(
    () => definitions.filter(d => d.section === sectionFilter),
    [definitions, sectionFilter],
  )

  const emptyCopy = setupEmptyStateCopy(sectionFilter)
  const addLabel = setupAddButtonLabel(sectionFilter)
  const deliveryBandLivePreview = formatSetupDeliveryBandLivePreview(form)
  const isFirstRun = !hasFridge

  const patchForm = patch => setForm(prev => ({ ...prev, ...patch }))

  const openCreate = (section = sectionFilter, { name } = {}) => {
    const next = visibleSections.includes(section)
      ? section
      : FOOD_SAFETY_SECTION.FRIDGES_FREEZERS
    setSectionFilter(next)
    setEditing(null)
    setForm({
      ...blankSetupForm(next),
      ...(name ? { name: String(name).trim() } : {}),
    })
    drawer.show()
  }

  const applyShoppingListSuggestion = (title, section = sectionFilter) => {
    const name = String(title || '').trim()
    if (!name) return
    if (drawer.open && !editing && form.section === section) {
      patchForm({ name })
      return
    }
    openCreate(section, { name })
  }

  const openEdit = definition => {
    setEditing(definition)
    setForm(formFromDefinition(definition))
    drawer.show()
  }

  const openDuplicate = definition => {
    setSectionFilter(definition.section)
    setEditing(null)
    setForm(duplicateSetupForm(definition))
    drawer.show()
  }

  const saveDefinition = async () => {
    const error = validateSetupForm(form)
    if (error) {
      message.warning(error)
      return
    }
    if (!saveLock.tryAcquire('definition')) return
    setSavingForm(true)
    try {
      const body = buildSetupPayload(form, { isCreate: !editing })
      try {
        if (editing) {
          await updateFoodSafetyDefinition(venueId, editing._id, body)
        } else {
          await createFoodSafetyDefinition(venueId, body)
        }
      } catch (err) {
        message.error(err?.message || 'Could not save.')
        return
      }
      message.success(
        setupSaveSuccessMessage({ isCreate: !editing, name: form.name }),
      )
      trackFoodSafetySetupSaved({
        venueId,
        section: form.section,
        action: editing ? 'update' : 'create',
      })
      drawer.hide()
      await refreshAfterSave()
    } finally {
      saveLock.release('definition')
      setSavingForm(false)
    }
  }

  const outOfService = useFoodSafetyOutOfService({
    venueId,
    onChanged: fetchSetup,
    fallbackFocus: findSetupAddButton,
    canManage: !blocked,
  })
  const todayLocalDate = moment().format('YYYY-MM-DD')

  const onArchive = definition => {
    confirmWithFocusReturn(
      {
        title: `Archive ${definition.name}?`,
        content: 'It leaves today’s checks. Existing logs stay in history.',
        okText: 'Archive',
        okType: 'danger',
        onOk: () =>
          saveLock.run(`archive:${definition._id}`, async () => {
            try {
              await archiveFoodSafetyDefinition(venueId, definition._id)
            } catch (err) {
              message.error(err?.message || 'Could not archive.')
              return
            }
            message.success(`Archived ${definition.name}`)
            await refreshAfterSave()
          }),
      },
      { fallbackFocus: findSetupAddButton },
    )
  }

  const savedEmails = resolveFoodSafetyManagerReportEmails(settings)
  const canSaveEmails = canSaveFoodSafetyManagerEmails(
    emailDrafts,
    savedEmails,
    pendingEmailInput,
  )

  const savedOpeningDays = resolveOpeningWeekdays(settings)
  const openingDaysLine = formatOpeningDaysClosedLine(openingDraft)
  const closedDates = readFoodSafetyClosedDates(settings)
  const canSaveOpeningDays =
    openingDraft.length > 0 &&
    !openingWeekdaysEqual(openingDraft, savedOpeningDays)

  const saveOpeningDays = async () => {
    if (!openingDraft.length) {
      message.warning('Choose at least one open day.')
      return
    }
    if (!saveLock.tryAcquire('opening-days')) return
    setSavingOpeningDays(true)
    try {
      const data = await updateFoodSafetySettings(venueId, {
        operatingWeekdays: openingDraft,
      })
      setSettings(data)
      setOpeningDraft(resolveOpeningWeekdays(data))
      message.success('Opening days saved')
    } catch (err) {
      message.error(err?.message || 'Could not save opening days.')
    } finally {
      setSavingOpeningDays(false)
      saveLock.release('opening-days')
    }
  }

  const saveTwiceDaily = async checked => {
    if (!saveLock.tryAcquire('fridge-twice-daily')) return
    setSavingTwiceDaily(true)
    try {
      const data = await updateFoodSafetySettings(venueId, {
        fridgeMorningAndAfternoon: checked === true,
      })
      setSettings(data)
    } catch (err) {
      message.error(err?.message || 'Could not save fridge checks.')
    } finally {
      setSavingTwiceDaily(false)
      saveLock.release('fridge-twice-daily')
    }
  }

  const saveClosedDates = async nextDates => {
    if (!saveLock.tryAcquire('closed-dates')) return
    setSavingClosedDate(true)
    try {
      const data = await updateFoodSafetySettings(venueId, {
        closedDates: nextDates,
      })
      setSettings(data)
      setClosedDateDraft('')
    } catch (err) {
      message.error(err?.message || 'Could not save that date.')
    } finally {
      saveLock.release('closed-dates')
      setSavingClosedDate(false)
    }
  }

  const addClosedDate = () => {
    const next = foodSafetyClosedDatesAfterAdd(closedDates, closedDateDraft)
    if (next.error) {
      message.error(next.error)
      return
    }
    saveClosedDates(next.dates)
  }

  const removeClosedDate = localDate => {
    saveClosedDates(foodSafetyClosedDatesAfterRemove(closedDates, localDate))
  }

  const saveEmail = async () => {
    const { list, pending } = mergeFoodSafetyManagerEmailPending(
      emailDrafts,
      pendingEmailInput,
    )
    if (pending === 'invalid') {
      message.warning('Enter a valid email address.')
      return
    }
    if (!saveLock.tryAcquire('emails')) return
    setSavingEmail(true)
    try {
      const data = await updateFoodSafetySettings(venueId, {
        managerReportEmails: list,
      })
      setSettings(data)
      setEmailDrafts(resolveFoodSafetyManagerReportEmails(data))
      setPendingEmailInput('')
      message.success(
        list.length > 1
          ? 'Manager report emails saved'
          : 'Manager report email saved',
      )
    } catch (err) {
      message.error(err?.message || 'Could not save email.')
    } finally {
      saveLock.release('emails')
      setSavingEmail(false)
    }
  }

  if (blocked) {
    return (
      <PermissionBlockScreen
        description={foodSafetyManageBlockedCopy(userRole, customRoles)}
      />
    )
  }
  // Spinner on first load only — a reload must not unmount the page, its
  // scroll position or an open drawer.
  if (!venueId || (loading && !loadedOnce)) return <Loading />

  const isScheduled =
    resolveSetupScheduleMode(form) === FOOD_SAFETY_SCHEDULE_MODE.SCHEDULED
  const isProbeForm = [
    FOOD_SAFETY_SECTION.PROBE_CALIBRATION,
    FOOD_SAFETY_SECTION.DISHWASHERS,
  ].includes(form.section)

  const renderSectionTabs = ({ testIdPrefix, tabsRef }) => (
    <SectionTabs ref={tabsRef} data-testid={testIdPrefix}>
      {visibleSections.map(key => (
        <TabChip
          key={key}
          type='button'
          data-active={sectionFilter === key ? 'true' : 'false'}
          data-testid={`${testIdPrefix}-${key}`}
          onClick={() => setSectionFilter(key)}
        >
          {FOOD_SAFETY_SECTION_LABELS[key]}
          {isFoodSafetyOptionalSetupSection(key) ? (
            <TabOptional> optional</TabOptional>
          ) : null}
        </TabChip>
      ))}
    </SectionTabs>
  )

  return (
    <Wrapper>
      <TopBar>
        <BackLink to={`/v/${venueId}/food-safety`}>
          <LeftOutlined /> Today’s overview
        </BackLink>
      </TopBar>

      <FoodSafetyHeroBand>
        <FoodSafetyHeaderRow>
          <div>
            <FoodSafetyPageTitle>Food Safety setup</FoodSafetyPageTitle>
            <SetupIntro>
              {isFirstRun
                ? 'Start with your fridges, then add a closing checklist. Takes a couple of minutes.'
                : 'Units, checklists, and optional saved names for the kitchen day.'}
            </SetupIntro>
            <CoachAskLink
              question={FOOD_SAFETY_OLLIE_COACH_DRAFT}
              label={FOOD_SAFETY_OLLIE_COACH_LABEL}
              data-testid='food-safety-setup-ask-ollie'
              onOpen={() =>
                trackFoodSafetyOllieOpened({ venueId, surface: 'setup' })
              }
            />
          </div>
          <Button
            type='primary'
            icon='plus'
            onClick={() => openCreate()}
            data-testid='fs-setup-add'
          >
            {addLabel}
          </Button>
        </FoodSafetyHeaderRow>
      </FoodSafetyHeroBand>

      {renderSectionTabs({ testIdPrefix: 'fs-setup-tab-top' })}

      {isFirstRun ? (
        <FirstRunCard data-testid='fs-setup-first-run'>
          <H3>First-time setup</H3>
          <BodyText>
            Start with each fridge and freezer, then add a closing checklist.
            Cooking, cooling, holding, deliveries, and probes unlock after your
            first fridge.
          </BodyText>
          <FirstRunActions>
            <Button
              type='primary'
              onClick={() => openCreate(FOOD_SAFETY_SECTION.FRIDGES_FREEZERS)}
            >
              Add fridge or freezer
            </Button>
            <Button
              onClick={() =>
                openCreate(FOOD_SAFETY_SECTION.CLEANING_CHECKLISTS)
              }
            >
              {setupAddButtonLabel(FOOD_SAFETY_SECTION.CLEANING_CHECKLISTS)}
            </Button>
          </FirstRunActions>
        </FirstRunCard>
      ) : null}

      <FoodSafetySetupReadinessCard
        venueId={venueId}
        refreshKey={readinessRefreshKey}
        visibleSections={visibleSections}
        onOpenSection={section => {
          setSectionFilter(section)
          scrollToSetupRef(sectionTabsRef)
        }}
        onOpenRecipients={() => scrollToSetupRef(emailsCardRef)}
      />

      <SettingsCard ref={emailsCardRef} data-testid='fs-manager-emails-card'>
        <H3 id='fs-manager-emails-title'>Manager report emails</H3>
        <BodyText>
          Where PDF reports are emailed from Reports. Add every manager who
          should receive them — same idea as restricted-supplier approvals.
          Optional until you need it (up to{' '}
          {FOOD_SAFETY_MANAGER_REPORT_EMAIL_MAX}
          ).
        </BodyText>
        <EmailRow>
          <EmailFieldWrap
            ref={emailFieldRef}
            data-testid='fs-manager-email-wrap'
            onInput={event => {
              if (event.target?.tagName === 'INPUT') {
                setPendingEmailInput(event.target.value || '')
              }
            }}
          >
            <ReactMultiEmail
              placeholder='manager@venue.com'
              emails={emailDrafts}
              onChange={list => {
                setEmailDrafts(
                  (list || []).slice(0, FOOD_SAFETY_MANAGER_REPORT_EMAIL_MAX),
                )
                setPendingEmailInput('')
              }}
              validateEmail={email => isEmail(email)}
              getLabel={(email, index, removeEmail) => (
                <EmailTag data-tag key={`${email}-${index}`}>
                  {email}
                  <button
                    type='button'
                    data-tag-handle
                    aria-label={`Remove ${email}`}
                    onClick={() => removeEmail(index)}
                  >
                    ×
                  </button>
                </EmailTag>
              )}
            />
          </EmailFieldWrap>
          <Button
            type={canSaveEmails ? 'primary' : 'default'}
            loading={savingEmail}
            onClick={saveEmail}
            disabled={!canSaveEmails}
            data-testid='fs-manager-email-save'
          >
            Save emails
          </Button>
        </EmailRow>
      </SettingsCard>

      <SettingsCard data-testid='fs-opening-days-card'>
        <H3>Opening days</H3>
        <BodyText data-testid='fs-opening-days-intro'>
          {formatOpeningDaysIntro(settings)}
        </BodyText>
        <PresetRow>
          {FOOD_SAFETY_OPENING_DAY_OPTIONS.map(option => {
            const open = openingDraft.includes(option.value)
            return (
              <PresetChip
                key={option.value}
                type='button'
                data-active={open ? 'true' : 'false'}
                aria-pressed={open}
                aria-label={`${option.name}, ${open ? 'open' : 'closed'}`}
                data-testid={`fs-opening-day-${option.value}`}
                onClick={() =>
                  setOpeningDraft(current =>
                    toggleOpeningWeekday(current, option.value),
                  )
                }
              >
                {option.label}
              </PresetChip>
            )
          })}
        </PresetRow>
        {openingDaysLine ? (
          <Hint data-testid='fs-opening-days-line'>{openingDaysLine}</Hint>
        ) : null}
        <div>
          <Button
            type={canSaveOpeningDays ? 'primary' : 'default'}
            loading={savingOpeningDays}
            disabled={!canSaveOpeningDays}
            onClick={saveOpeningDays}
            data-testid='fs-opening-days-save'
          >
            Save days
          </Button>
        </div>
        <ClosedDateBlock data-testid='fs-closed-dates'>
          <H3>Closed on a date</H3>
          <BodyText>
            One day the kitchen is shut. The days above stay as they are.
          </BodyText>
          <ClosedDateRow>
            <Input
              type='date'
              value={closedDateDraft}
              onChange={event => setClosedDateDraft(event.target.value)}
              aria-label='Closed on a date'
              data-testid='fs-closed-date-input'
              style={{ maxWidth: '14rem' }}
            />
            <Button
              onClick={addClosedDate}
              loading={savingClosedDate}
              disabled={!closedDateDraft || savingClosedDate}
              data-testid='fs-closed-date-add'
            >
              Add date
            </Button>
          </ClosedDateRow>
          {closedDates.map(date => (
            <ClosedDateRow key={date} data-testid={`fs-closed-date-${date}`}>
              <BodyText>{formatFoodSafetyClosedDate(date)}</BodyText>
              <Button
                type='link'
                onClick={() => removeClosedDate(date)}
                disabled={savingClosedDate}
                data-testid={`fs-closed-date-remove-${date}`}
              >
                Remove
              </Button>
            </ClosedDateRow>
          ))}
        </ClosedDateBlock>
      </SettingsCard>

      {renderSectionTabs({
        testIdPrefix: 'fs-setup-tab',
        tabsRef: sectionTabsRef,
      })}

      {deferAdHocTemplates ? (
        <DeferHint data-testid='fs-setup-templates-deferred'>
          {FOOD_SAFETY_SETUP_TEMPLATES_DEFERRED_HINT}
        </DeferHint>
      ) : null}

      {isFoodSafetyHoldingSection(sectionFilter) ? (
        <FoodSafetyHoldingRangeCard
          venueId={venueId}
          section={sectionFilter}
          range={settings?.holdingRanges?.[sectionFilter]}
          onSaved={data => data && setSettings(data)}
        />
      ) : null}

      {sectionFilter === FOOD_SAFETY_SECTION.COOLING ? (
        <FoodSafetyCoolingTargetCard
          venueId={venueId}
          target={settings?.coolingTarget}
          remindAfterMinutes={settings?.coolingRemindAfterMinutes}
          onSaved={data => data && setSettings(data)}
        />
      ) : null}

      {isFoodSafetyOptionalSetupSection(sectionFilter) ? (
        <QuickPickHint data-testid='fs-setup-quick-pick-hint'>
          {FOOD_SAFETY_QUICK_PICK_HINT}
        </QuickPickHint>
      ) : null}

      {isFoodSafetyShoppingListSuggestSection(sectionFilter) ? (
        <FoodSafetyShoppingListSuggestBlock
          mode='setup'
          framed
          section={sectionFilter}
          products={venueProducts}
          recipes={recipes}
          existingDefinitions={definitions}
          resampleKey={sectionFilter}
          title='Products you might not want to forget'
          markOptional
          hint='Search or tap to start a shortcut. The name on the day stays open for something that was never ordered through OMS.'
          onSelect={title => applyShoppingListSuggestion(title, sectionFilter)}
          testId='fs-setup-shopping-suggest'
        />
      ) : null}

      {sectionFilter === FOOD_SAFETY_SECTION.FRIDGES_FREEZERS ? (
        <TwiceDailyRow data-testid='fs-fridge-twice-daily'>
          <div>
            <TwiceDailyTitle>
              {FOOD_SAFETY_FRIDGE_TWICE_DAILY_LABEL}
            </TwiceDailyTitle>
            <BodyText>{FOOD_SAFETY_FRIDGE_TWICE_DAILY_HINT}</BodyText>
          </div>
          <Switch
            checked={settings.fridgeMorningAndAfternoon === true}
            loading={savingTwiceDaily}
            onChange={saveTwiceDaily}
            aria-label={FOOD_SAFETY_FRIDGE_TWICE_DAILY_LABEL}
          />
        </TwiceDailyRow>
      ) : null}

      {filtered.length === 0 ? (
        <EmptyCard data-testid='fs-setup-empty'>
          <H3>{emptyCopy.title}</H3>
          <BodyText>{emptyCopy.body}</BodyText>
          <Button
            type='primary'
            icon='plus'
            onClick={() => openCreate()}
            data-testid='fs-setup-empty-add'
          >
            {addLabel}
          </Button>
        </EmptyCard>
      ) : (
        <DefList>
          {filtered.map(def => {
            const deliveryBand = formatSetupDeliveryBandLabel(def)
            const pausable = canMarkFoodSafetyOutOfService(def)
            const openPeriod = pausable
              ? getOpenFoodSafetyOutOfServicePeriod(def)
              : null
            return (
              <DefCard
                key={String(def._id)}
                data-testid={`fs-def-${def._id}`}
                data-out-of-service={openPeriod ? 'true' : 'false'}
              >
                <div>
                  <DefNameRow>
                    <DefName>{def.name}</DefName>
                    {deliveryBand ? (
                      <DefBand
                        data-testid={`fs-def-band-${def._id}`}
                        data-band={
                          (def.config && def.config.tempBand) || 'custom'
                        }
                      >
                        {deliveryBand}
                      </DefBand>
                    ) : null}
                    {openPeriod ? (
                      <DefOutOfService
                        data-testid={`fs-def-out-of-service-${def._id}`}
                      >
                        Out of service
                      </DefOutOfService>
                    ) : null}
                  </DefNameRow>
                  <DefMeta>{formatDefinitionSummary(def)}</DefMeta>
                  {def.section === FOOD_SAFETY_SECTION.FRIDGES_FREEZERS ? (
                    <DefMeta data-testid={`fs-def-five-readings-${def._id}`}>
                      {formatFridgeFiveReadingsCopy(
                        settings.fridgeMorningAndAfternoon === true,
                      )}
                    </DefMeta>
                  ) : null}
                  {openPeriod ? (
                    <DefMeta
                      data-testid={`fs-def-out-of-service-since-${def._id}`}
                    >
                      {formatFoodSafetyOutOfServiceSince(openPeriod, {
                        todayLocalDate,
                      })}
                    </DefMeta>
                  ) : null}
                </div>
                <DefActions>
                  <Button
                    size='small'
                    onClick={() => openEdit(def)}
                    aria-label={`Edit ${def.name}`}
                    data-testid={`fs-setup-edit-${def._id}`}
                  >
                    Edit
                  </Button>
                  {def.section === FOOD_SAFETY_SECTION.FRIDGES_FREEZERS ? (
                    <Button
                      size='small'
                      onClick={() => openDuplicate(def)}
                      aria-label={`Duplicate ${def.name}`}
                      data-testid={`fs-setup-duplicate-${def._id}`}
                    >
                      Duplicate
                    </Button>
                  ) : null}
                  {pausable ? (
                    openPeriod ? (
                      <Button
                        size='small'
                        onClick={() => outOfService.confirmReturn(def)}
                        aria-label={`Return to service: ${def.name}`}
                        data-testid={`fs-def-return-to-service-${def._id}`}
                      >
                        Return to service
                      </Button>
                    ) : (
                      <Button
                        size='small'
                        onClick={() => outOfService.openMark(def)}
                        aria-label={`Mark ${def.name} out of service`}
                        data-testid={`fs-def-mark-out-of-service-${def._id}`}
                      >
                        Out of service
                      </Button>
                    )
                  ) : null}
                  <Button
                    size='small'
                    type='danger'
                    ghost
                    onClick={() => onArchive(def)}
                    aria-label={`Archive ${def.name}`}
                    data-testid={`fs-setup-archive-${def._id}`}
                  >
                    Archive
                  </Button>
                </DefActions>
              </DefCard>
            )
          })}
        </DefList>
      )}

      {drawer.mounted ? (
        <Drawer
          title={setupDrawerTitle({ editing, section: form.section })}
          width={foodSafetyDrawerWidth(420)}
          visible={drawer.open}
          onClose={drawer.hide}
          afterVisibleChange={drawer.afterVisibleChange}
          destroyOnClose
        >
          <FormStack>
            {!editing ? (
              <Field>
                <Label>Section</Label>
                <Select
                  aria-label='Section'
                  value={form.section}
                  onChange={value => setForm(blankSetupForm(value))}
                  style={{ width: '100%' }}
                >
                  {visibleSections.map(key => (
                    <Option key={key} value={key}>
                      {FOOD_SAFETY_SECTION_LABELS[key]}
                    </Option>
                  ))}
                </Select>
              </Field>
            ) : null}

            {form.section === FOOD_SAFETY_SECTION.CLEANING_CHECKLISTS ? (
              <Field>
                <Label id='fs-setup-task-kind-label'>Job or checklist</Label>
                <PresetRow
                  role='group'
                  aria-labelledby='fs-setup-task-kind-label'
                >
                  <PresetChip
                    type='button'
                    data-active={
                      form.taskKind === FOOD_SAFETY_TASK_KIND.JOB
                        ? 'true'
                        : 'false'
                    }
                    data-testid='fs-setup-task-kind-job'
                    onClick={() =>
                      patchForm({ taskKind: FOOD_SAFETY_TASK_KIND.JOB })
                    }
                  >
                    Job
                  </PresetChip>
                  <PresetChip
                    type='button'
                    data-active={
                      form.taskKind === FOOD_SAFETY_TASK_KIND.JOB
                        ? 'false'
                        : 'true'
                    }
                    data-testid='fs-setup-task-kind-checklist'
                    onClick={() =>
                      patchForm({ taskKind: FOOD_SAFETY_TASK_KIND.CHECKLIST })
                    }
                  >
                    Checklist
                  </PresetChip>
                </PresetRow>
                <Hint data-testid='fs-setup-task-kind-hint'>
                  {isCleaningJob(form)
                    ? 'One sign-off. Name, how often, and a due time.'
                    : 'Staff tick off each step.'}
                </Hint>
              </Field>
            ) : null}

            <Field>
              <Label as='label' htmlFor='fs-setup-name-field'>
                Name
              </Label>
              <Input
                id='fs-setup-name-field'
                value={form.name}
                onChange={e => patchForm({ name: e.target.value })}
                placeholder={
                  form.section === FOOD_SAFETY_SECTION.FRIDGES_FREEZERS
                    ? 'e.g. Walk-in fridge'
                    : form.section === FOOD_SAFETY_SECTION.CLEANING_CHECKLISTS
                    ? isCleaningJob(form)
                      ? 'e.g. Deep clean floors'
                      : 'e.g. Closing checklist'
                    : isProbeForm
                    ? isDishwasherUnit(form)
                      ? 'e.g. Dishwasher 1'
                      : 'e.g. Probe 1'
                    : isFoodSafetyHoldingSection(form.section)
                    ? FOOD_SAFETY_ADHOC_SECTION_META[form.section]
                        .namePlaceholder
                    : 'e.g. Chicken curry'
                }
                data-testid='fs-setup-name'
              />
            </Field>

            {isFoodSafetyHoldingSection(form.section) ? (
              <Hint data-testid='fs-setup-holding-range-hint'>
                Checked against the venue{' '}
                {FOOD_SAFETY_SECTION_LABELS[form.section].toLowerCase()} limit (
                {formatFoodSafetyHoldingRange(
                  settings?.holdingRanges?.[form.section] ||
                    FOOD_SAFETY_HOLDING_SUGGESTED_RANGES[form.section],
                )}
                ) — change it on the {FOOD_SAFETY_SECTION_LABELS[form.section]}{' '}
                tab.
              </Hint>
            ) : null}

            {!editing &&
            isFoodSafetyShoppingListSuggestSection(form.section) ? (
              <FoodSafetyShoppingListSuggestBlock
                mode='setup'
                section={form.section}
                products={venueProducts}
                recipes={recipes}
                existingDefinitions={definitions}
                resampleKey={`${drawer.open ? 'open' : 'closed'}-${
                  form.section
                }`}
                selectedTitle={form.name}
                title='From your shopping list'
                onSelect={title =>
                  applyShoppingListSuggestion(title, form.section)
                }
                testId='fs-setup-drawer-suggest'
              />
            ) : null}

            {form.section === FOOD_SAFETY_SECTION.FRIDGES_FREEZERS ? (
              <>
                <Field>
                  <Label>Unit type</Label>
                  <Select
                    aria-label='Unit type'
                    value={form.unitType}
                    onChange={unitType => patchForm({ unitType })}
                    style={{ width: '100%' }}
                  >
                    <Option value='fridge'>Fridge</Option>
                    <Option value='freezer'>Freezer</Option>
                  </Select>
                </Field>
                <InlineFields>
                  <Field>
                    <Label as='label' htmlFor='fs-setup-temp-min'>
                      Min °C
                    </Label>
                    <InputNumber
                      id='fs-setup-temp-min'
                      value={form.tempMin}
                      onChange={tempMin => patchForm({ tempMin })}
                      style={{ width: '100%' }}
                    />
                  </Field>
                  <Field>
                    <Label as='label' htmlFor='fs-setup-temp-max'>
                      Max °C
                    </Label>
                    <InputNumber
                      id='fs-setup-temp-max'
                      value={form.tempMax}
                      onChange={tempMax => patchForm({ tempMax })}
                      style={{ width: '100%' }}
                    />
                  </Field>
                </InlineFields>
              </>
            ) : null}

            {form.section === FOOD_SAFETY_SECTION.COOKING_REHEATING ? (
              <>
                <Field>
                  <Label>Kind</Label>
                  <Select
                    aria-label='Kind'
                    value={form.cookingKind}
                    onChange={cookingKind => patchForm({ cookingKind })}
                    style={{ width: '100%' }}
                  >
                    <Option value='cooking'>Cooking</Option>
                    <Option value='reheating'>Reheating</Option>
                  </Select>
                </Field>
                <Field>
                  <Label as='label' htmlFor='fs-setup-cooking-temp-min'>
                    Min core temp °C (optional)
                  </Label>
                  <InputNumber
                    id='fs-setup-cooking-temp-min'
                    value={form.cookingTempMin}
                    onChange={cookingTempMin => patchForm({ cookingTempMin })}
                    style={{ width: '100%' }}
                  />
                </Field>
              </>
            ) : null}

            {form.section === FOOD_SAFETY_SECTION.COOLING ? (
              <Field>
                <Label as='label' htmlFor='fs-setup-cooling-temp-max'>
                  Max final cooled temp °C (optional)
                </Label>
                <InputNumber
                  id='fs-setup-cooling-temp-max'
                  value={form.tempMax}
                  onChange={tempMax => patchForm({ tempMax })}
                  placeholder='Blank uses the venue cooling target'
                  style={{ width: '100%' }}
                />
              </Field>
            ) : null}

            {isProbeForm ? (
              <>
                {isDishwasherUnit(form) ? (
                  <>
                    <Hint>
                      Set the limits for this machine from its manufacturer
                      instructions and your food safety procedure. 82°C is a
                      suggested thermal final-rinse target, not a universal
                      requirement.
                    </Hint>
                    <InlineFields>
                      <Field>
                        <Label
                          as='label'
                          htmlFor='fs-setup-probe-reference-temp-field'
                        >
                          Wash at or above °C
                        </Label>
                        <InputNumber
                          id='fs-setup-probe-reference-temp-field'
                          value={form.referenceTemp}
                          onChange={referenceTemp =>
                            patchForm({ referenceTemp })
                          }
                          step={0.1}
                          style={{ width: '100%' }}
                          data-testid='fs-setup-probe-reference-temp'
                        />
                      </Field>
                      <Field>
                        <Label
                          as='label'
                          htmlFor='fs-setup-probe-boiling-temp-field'
                        >
                          Final rinse at or above °C
                        </Label>
                        <InputNumber
                          id='fs-setup-probe-boiling-temp-field'
                          value={form.boilingReferenceTemp}
                          onChange={boilingReferenceTemp =>
                            patchForm({ boilingReferenceTemp })
                          }
                          step={0.1}
                          style={{ width: '100%' }}
                          data-testid='fs-setup-probe-boiling-temp'
                        />
                      </Field>
                    </InlineFields>
                    {form.referenceTemp != null &&
                    form.boilingReferenceTemp != null ? (
                      <BandPreview data-testid='fs-setup-probe-preview'>
                        Passes when it reads {formatProbeExpected(form)}
                      </BandPreview>
                    ) : null}
                  </>
                ) : (
                  <>
                    <Field>
                      <Label
                        as='label'
                        htmlFor='fs-setup-probe-reference-field'
                      >
                        Serial or tag (optional)
                      </Label>
                      <Input
                        id='fs-setup-probe-reference-field'
                        value={form.probeReference}
                        maxLength={FOOD_SAFETY_PROBE_REFERENCE_MAX_LENGTH}
                        onChange={e =>
                          patchForm({ probeReference: e.target.value })
                        }
                        placeholder='e.g. SN 40213'
                        data-testid='fs-setup-probe-reference'
                      />
                    </Field>
                    <Field>
                      <Label>Check</Label>
                      <PresetRow role='group' aria-label='Calibration check'>
                        {FOOD_SAFETY_PROBE_TEST_OPTIONS.map(
                          ({ test, label }) => (
                            <PresetChip
                              key={test}
                              type='button'
                              data-active={
                                form.calibrationTest === test ? 'true' : 'false'
                              }
                              data-testid={`fs-probe-test-${test}`}
                              onClick={() => patchForm(probeTestPreset(test))}
                            >
                              {label}
                            </PresetChip>
                          ),
                        )}
                      </PresetRow>
                    </Field>
                    <InlineFields>
                      <Field>
                        <Label
                          as='label'
                          htmlFor='fs-setup-probe-reference-temp-field'
                        >
                          {isProbeBothTests(form)
                            ? 'Ice water should read °C'
                            : 'Should read °C'}
                        </Label>
                        <InputNumber
                          id='fs-setup-probe-reference-temp-field'
                          value={form.referenceTemp}
                          onChange={referenceTemp =>
                            patchForm({ referenceTemp })
                          }
                          step={0.1}
                          style={{ width: '100%' }}
                          data-testid='fs-setup-probe-reference-temp'
                        />
                      </Field>
                      {isProbeBothTests(form) ? (
                        <Field>
                          <Label
                            as='label'
                            htmlFor='fs-setup-probe-boiling-temp-field'
                          >
                            Boiling water should read °C
                          </Label>
                          <InputNumber
                            id='fs-setup-probe-boiling-temp-field'
                            value={form.boilingReferenceTemp}
                            onChange={boilingReferenceTemp =>
                              patchForm({ boilingReferenceTemp })
                            }
                            step={0.1}
                            style={{ width: '100%' }}
                            data-testid='fs-setup-probe-boiling-temp'
                          />
                        </Field>
                      ) : null}
                      <Field>
                        <Label
                          as='label'
                          htmlFor='fs-setup-probe-tolerance-field'
                        >
                          Allowed difference ± °C
                        </Label>
                        <InputNumber
                          id='fs-setup-probe-tolerance-field'
                          value={form.tolerance}
                          onChange={tolerance => patchForm({ tolerance })}
                          min={FOOD_SAFETY_CALIBRATION_TOLERANCE_BOUNDS.min}
                          max={FOOD_SAFETY_CALIBRATION_TOLERANCE_BOUNDS.max}
                          step={0.1}
                          style={{ width: '100%' }}
                          data-testid='fs-setup-probe-tolerance'
                        />
                      </Field>
                    </InlineFields>
                    {form.referenceTemp != null && form.tolerance != null ? (
                      <BandPreview data-testid='fs-setup-probe-preview'>
                        Passes when it reads {formatProbeExpected(form)}
                      </BandPreview>
                    ) : null}
                  </>
                )}
                <Checkbox
                  checked={Boolean(form.probeScheduled)}
                  onChange={e =>
                    patchForm(setupProbeSchedulePatch(form, e.target.checked))
                  }
                >
                  Check on a schedule
                </Checkbox>
              </>
            ) : null}

            {form.section === FOOD_SAFETY_SECTION.DELIVERIES ? (
              <>
                <Field>
                  <Label>Target band</Label>
                  <PresetRow
                    role='group'
                    aria-label='Delivery temperature band'
                  >
                    {Object.values(FOOD_SAFETY_DELIVERY_TEMP_BAND).map(band => {
                      const preset = FOOD_SAFETY_DELIVERY_TEMP_PRESETS[band]
                      const active = form.deliveryTempBand === band
                      const rangeSuffix = formatSetupDeliveryBandChipRange(
                        band,
                        form,
                      )
                      return (
                        <PresetChip
                          key={band}
                          type='button'
                          data-active={active ? 'true' : 'false'}
                          data-testid={`fs-delivery-band-${band}`}
                          onClick={() =>
                            patchForm(applyDeliveryTempPreset(band) || {})
                          }
                        >
                          {preset.label}
                          {rangeSuffix ? ` ${rangeSuffix}` : ''}
                        </PresetChip>
                      )
                    })}
                  </PresetRow>
                  {deliveryBandLivePreview ? (
                    <BandPreview data-testid='fs-delivery-band-preview'>
                      {deliveryBandLivePreview}
                    </BandPreview>
                  ) : null}
                  <Hint>
                    Chilled is the van default. Pick Frozen or Ambient when the
                    product needs a different check.
                  </Hint>
                </Field>
                {form.deliveryTempBand !==
                FOOD_SAFETY_DELIVERY_TEMP_BAND.AMBIENT ? (
                  <Field>
                    <Label as='label' htmlFor='fs-setup-temp-max'>
                      Arrives at or below °C
                    </Label>
                    <InputNumber
                      id='fs-setup-temp-max'
                      value={form.tempMax}
                      onChange={tempMax => patchForm({ tempMax })}
                      style={{ width: '100%' }}
                      data-testid='fs-setup-delivery-temp-max'
                    />
                    <Hint>
                      Only flagged when it arrives warmer than this — colder is
                      fine.
                    </Hint>
                  </Field>
                ) : (
                  <Hint>No temperature range — log temp optionally.</Hint>
                )}
              </>
            ) : null}

            {form.section === FOOD_SAFETY_SECTION.CLEANING_CHECKLISTS &&
            !isCleaningJob(form) ? (
              <Field>
                <Label id='fs-setup-steps-label'>Steps</Label>
                <StepsEditor
                  role='group'
                  aria-labelledby='fs-setup-steps-label'
                >
                  {(form.steps || []).map((step, index) => (
                    <StepRow key={step.key || index}>
                      <Input
                        value={step.label}
                        onChange={e => {
                          const steps = [...form.steps]
                          steps[index] = {
                            ...steps[index],
                            label: e.target.value,
                          }
                          patchForm({ steps })
                        }}
                        placeholder={`Step ${index + 1}`}
                        aria-label={`Step ${index + 1}`}
                      />
                      <Button
                        size='small'
                        aria-label={`Remove step ${index + 1}`}
                        disabled={(form.steps || []).length <= 1}
                        onClick={() =>
                          patchForm({
                            steps: form.steps.filter((_, i) => i !== index),
                          })
                        }
                      >
                        Remove
                      </Button>
                    </StepRow>
                  ))}
                  <Button
                    size='small'
                    icon='plus'
                    onClick={() =>
                      patchForm({
                        steps: [
                          ...(form.steps || []),
                          {
                            key: `step_${Date.now()}`,
                            label: '',
                          },
                        ],
                      })
                    }
                  >
                    Add step
                  </Button>
                </StepsEditor>
              </Field>
            ) : null}

            {isScheduled ? (
              <>
                <Field>
                  <Label>Frequency</Label>
                  <Select
                    aria-label='Frequency'
                    value={form.frequency}
                    onChange={frequency => patchForm({ frequency })}
                    style={{ width: '100%' }}
                  >
                    {Object.values(FOOD_SAFETY_FREQUENCY).map(value => (
                      <Option key={value} value={value}>
                        {FOOD_SAFETY_FREQUENCY_LABELS[value]}
                      </Option>
                    ))}
                  </Select>
                  {foodSafetySetupFrequencyHint(form) ? (
                    <Hint data-testid='fs-setup-frequency-hint'>
                      {foodSafetySetupFrequencyHint(form)}
                    </Hint>
                  ) : null}
                </Field>
                {form.frequency === FOOD_SAFETY_FREQUENCY.WEEKLY ? (
                  <Field>
                    <Label as='label' htmlFor='fs-setup-weekdays'>
                      Weekdays
                    </Label>
                    <Select
                      id='fs-setup-weekdays'
                      aria-label='Weekdays'
                      mode='multiple'
                      value={form.weekdays}
                      onChange={weekdays => patchForm({ weekdays })}
                      style={{ width: '100%' }}
                      placeholder='Select days'
                    >
                      {FOOD_SAFETY_WEEKDAY_OPTIONS.map(opt => (
                        <Option key={opt.value} value={opt.value}>
                          {opt.label}
                        </Option>
                      ))}
                    </Select>
                  </Field>
                ) : null}
                {form.section === FOOD_SAFETY_SECTION.FRIDGES_FREEZERS ? (
                  <Field>
                    <Label>Due times</Label>
                    {(form.dueTimes || [form.dueByTime || '10:00']).map(
                      (time, index) => (
                        <DueTimeRow key={`due-${index}`}>
                          <FoodSafetyClockField
                            id={
                              index === 0
                                ? 'fs-setup-due-by'
                                : `fs-setup-due-by-${index}`
                            }
                            value={time || ''}
                            onChange={value => {
                              const next = [
                                ...(form.dueTimes || [
                                  form.dueByTime || '10:00',
                                ]),
                              ]
                              next[index] = value
                              patchForm({
                                dueTimes: next,
                                dueByTime: next[0] || '',
                              })
                            }}
                            aria-label={`Due time ${index + 1}`}
                            data-testid={
                              index === 0
                                ? 'fs-setup-due-by'
                                : `fs-setup-due-by-${index}`
                            }
                          />
                          <Button
                            size='small'
                            aria-label={`Remove due time ${index + 1}`}
                            disabled={
                              (form.dueTimes || [form.dueByTime]).length <= 1
                            }
                            onClick={() => {
                              const next = (
                                form.dueTimes || [form.dueByTime]
                              ).filter((_, i) => i !== index)
                              patchForm({
                                dueTimes: next,
                                dueByTime: next[0] || '',
                              })
                            }}
                          >
                            Remove
                          </Button>
                        </DueTimeRow>
                      ),
                    )}
                    {(form.dueTimes || []).length <
                    FOOD_SAFETY_FRIDGE_MAX_DUE_TIMES ? (
                      <Button
                        size='small'
                        icon='plus'
                        onClick={() => {
                          const next = addFridgeDueTime(
                            form.dueTimes || [form.dueByTime || '10:00'],
                          )
                          patchForm({ dueTimes: next, dueByTime: next[0] })
                        }}
                        data-testid='fs-setup-add-due-time'
                      >
                        Add due time
                      </Button>
                    ) : null}
                    <Hint data-testid='fs-setup-due-times-hint'>
                      Each time is its own check. Staff can still add extra
                      readings (up to 5).
                    </Hint>
                  </Field>
                ) : (
                  <Field>
                    <Label as='label' htmlFor='fs-setup-due-by'>
                      Due by
                    </Label>
                    <FoodSafetyClockField
                      id='fs-setup-due-by'
                      value={form.dueByTime || ''}
                      onChange={dueByTime => patchForm({ dueByTime })}
                      aria-label='Due by'
                      data-testid='fs-setup-due-by'
                    />
                  </Field>
                )}
                <Field>
                  <Label as='label' htmlFor='fs-setup-starts-on'>
                    Schedule starts on (optional)
                  </Label>
                  <Input
                    id='fs-setup-starts-on'
                    type='date'
                    value={form.scheduleStartsOn || ''}
                    onChange={e =>
                      patchForm({ scheduleStartsOn: e.target.value })
                    }
                  />
                  <Hint data-testid='fs-setup-starts-on-hint'>
                    Used for weekly / fortnightly / monthly anchors. Defaults to{' '}
                    {editing ? 'tomorrow' : 'today'} if blank.
                  </Hint>
                </Field>
              </>
            ) : (
              <Hint data-testid='fs-setup-no-schedule-hint'>
                {formatSetupNoScheduleHint(form.section, form)}
              </Hint>
            )}

            {editing?._id && hasFoodSafetyRangeHistory(form.section) ? (
              <FoodSafetyRangeHistory
                venueId={venueId}
                definitionId={editing._id}
                section={form.section}
              />
            ) : null}

            <DrawerFooter>
              <Button onClick={() => drawer.hide()}>Cancel</Button>
              <Button
                type='primary'
                loading={savingForm}
                onClick={saveDefinition}
                data-testid='fs-setup-save'
              >
                {setupSaveButtonLabel(form, { editing: Boolean(editing) })}
              </Button>
            </DrawerFooter>
          </FormStack>
        </Drawer>
      ) : null}

      <FoodSafetyOutOfServiceDialog outOfService={outOfService} />
    </Wrapper>
  )
}

const Wrapper = styled(FadeIn)`
  width: 100%;
  max-width: 52rem;
  margin: 0 auto;
  padding: ${({ theme }) => theme.spacing.sm};
  box-sizing: border-box;

  ${({ theme }) => theme.device('md')`
    padding: ${theme.spacing.md};
  `};
`

const TopBar = styled.div`
  margin-bottom: ${({ theme }) => theme.spacing.sm};
`

const BackLink = styled(Link)`
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
  color: ${({ theme }) => theme.colours.lettuceGreen || '#7cb305'};
  font-weight: 600;
`

const SetupIntro = styled(BodyText)`
  display: block;
  margin: 0.35rem 0 0;
  color: ${FOOD_SAFETY_SECONDARY_TEXT};
`

const CoachAskLink = styled(OllieAskLink)`
  display: block;
  margin: ${({ theme }) => theme.spacing.xs} 0 0;
`

const SettingsCard = styled(FoodSafetySurfaceCard)`
  margin-bottom: ${({ theme }) => theme.spacing.md};
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.xs};
`

const EmailRow = styled.div`
  display: flex;
  gap: ${({ theme }) => theme.spacing.sm};
  flex-wrap: wrap;
  align-items: flex-start;
`

const EmailFieldWrap = styled.div`
  flex: 1 1 16rem;
  min-width: 12rem;

  .react-multi-email {
    min-height: 40px;
    border: 1px solid ${({ theme }) => theme.colours.panSilver || '#d9d9d9'};
    border-radius: 6px;
    padding: 4px 8px;
    background: #fff;
  }

  .react-multi-email.focused {
    border-color: ${({ theme }) => theme.colours.lettuceGreen || '#7cb305'};
    box-shadow: 0 0 0 2px rgba(124, 179, 5, 0.15);
  }
`

const EmailTag = styled.div`
  display: inline-flex;
  align-items: center;
  padding: 2px 8px;
  margin: 2px;
  background-color: #f0f0f0;
  border-radius: 12px;
  font-size: 12px;

  [data-tag-handle] {
    margin-left: 6px;
    padding: 0;
    border: none;
    background: transparent;
    font: inherit;
    line-height: 1;
    cursor: pointer;
  }
`

const SectionTabs = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.4rem;
  margin-bottom: ${({ theme }) => theme.spacing.sm};
`

const TwiceDailyRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  margin-bottom: 1rem;
  padding: 0.85rem 1rem;
  border: 1px solid ${({ theme }) => theme.colours.panSilver || '#d9d9d9'};
  border-radius: 8px;
`

const TwiceDailyTitle = styled.div`
  font-weight: 700;
  margin-bottom: 0.25rem;
`

const QuickPickHint = styled.p`
  margin: 0 0 ${({ theme }) => theme.spacing.md};
  font-size: 0.875rem;
  color: ${FOOD_SAFETY_SECONDARY_TEXT};
`

const DeferHint = styled.p`
  margin: 0 0 ${({ theme }) => theme.spacing.md};
  font-size: 0.85rem;
  color: ${FOOD_SAFETY_SECONDARY_TEXT};
  line-height: 1.4;
`

const TabOptional = styled.span`
  margin-left: 0.35rem;
  font-size: 0.7rem;
  font-weight: 500;
  color: ${FOOD_SAFETY_SECONDARY_TEXT};
`

const TabChip = styled.button`
  border: 1px solid ${({ theme }) => theme.colours.panSilver || '#ddd'};
  background: #fff;
  border-radius: ${FOOD_SAFETY_SURFACE.CONTROL_RADIUS};
  padding: 0.3rem 0.75rem;
  cursor: pointer;
  font-size: 0.85rem;

  &[data-active='true'] {
    border-color: ${FOOD_SAFETY_SURFACE.LIME_EDGE};
    background: ${FOOD_SAFETY_SURFACE.LIME_WASH};
    font-weight: 600;
  }
`

const EmptyCard = styled(FoodSafetySurfaceCard)`
  border-style: dashed;
  margin-bottom: ${({ theme }) => theme.spacing.md};
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: ${({ theme }) => theme.spacing.sm};
`

const FirstRunCard = styled(FoodSafetySurfaceCard)`
  margin-bottom: ${({ theme }) => theme.spacing.md};
  border-color: ${FOOD_SAFETY_SURFACE.LIME_EDGE};
  background: ${FOOD_SAFETY_SURFACE.LIME_WASH};
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.xs};
`

const FirstRunActions = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: ${({ theme }) => theme.spacing.sm};
  margin-top: ${({ theme }) => theme.spacing.xs};
`

const DefList = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.sm};
`

const DefCard = styled(FoodSafetySurfaceCard)`
  display: flex;
  justify-content: space-between;
  gap: ${({ theme }) => theme.spacing.sm};
  align-items: center;
`

const DefNameRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 0.4rem 0.55rem;
`

const DefName = styled.div`
  font-weight: 600;
`

const DefBand = styled.span`
  display: inline-flex;
  align-items: center;
  padding: 0.1rem 0.45rem;
  border-radius: ${FOOD_SAFETY_SURFACE.CONTROL_RADIUS};
  border: 1px solid ${FOOD_SAFETY_SURFACE.LIME_EDGE};
  background: ${FOOD_SAFETY_SURFACE.LIME_WASH};
  font-size: 0.75rem;
  font-weight: 600;
  letter-spacing: 0.01em;
  color: ${({ theme }) => theme.colours.seaweedGreen || '#132d07'};
  line-height: 1.3;
`

const DefOutOfService = styled.span`
  font-size: 0.75rem;
  font-weight: 700;
  color: #5c5c5c;
  background: #ececec;
  border-radius: 999px;
  padding: 0.1rem 0.5rem;
`

const DefMeta = styled.div`
  font-size: 0.85rem;
  color: ${({ theme }) => theme.colours.oliveGrey || '#6b6b6b'};
`

const DefActions = styled.div`
  display: flex;
  flex-wrap: wrap;
  justify-content: flex-end;
  gap: 0.4rem;
  flex-shrink: 0;
`

const FormStack = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.sm};
`

const Field = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
`

const DueTimeRow = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
`

const InlineFields = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: ${({ theme }) => theme.spacing.sm};
`

const Label = styled.span`
  font-size: 0.8rem;
  font-weight: 600;
  color: ${({ theme }) => theme.colours.oliveGrey || '#6b6b6b'};
`

const ClosedDateBlock = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.55rem;
  margin-top: 0.4rem;
  padding-top: 0.85rem;
  border-top: 1px solid ${({ theme }) => theme.colours.mayoBorder || '#e8e8e8'};
`

const ClosedDateRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 0.5rem;
`

const PresetRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.4rem;
`

const PresetChip = styled.button`
  appearance: none;
  border: 1px solid
    ${({ theme, ...p }) =>
      p['data-active'] === 'true'
        ? theme.colours.lettuceGreen || '#7cb305'
        : theme.colours.mayoBorder || '#d9d9d9'};
  background: ${({ theme, ...p }) =>
    p['data-active'] === 'true'
      ? theme.colours.lettuceGreenLight || '#f6ffed'
      : '#fff'};
  color: ${({ theme }) => theme.colours.charcoal || '#262626'};
  border-radius: 999px;
  padding: 0.3rem 0.7rem;
  font-size: 0.8rem;
  cursor: pointer;
  line-height: 1.3;
`

const Hint = styled.p`
  margin: 0;
  font-size: 0.8rem;
  color: ${({ theme }) => theme.colours.oliveGrey || '#6b6b6b'};
`

const BandPreview = styled.p`
  margin: 0.45rem 0 0;
  font-size: 0.85rem;
  font-weight: 600;
  color: ${({ theme }) => theme.colours.charcoal || '#262626'};
`

const StepsEditor = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.4rem;
`

const StepRow = styled.div`
  display: flex;
  gap: 0.4rem;
`

const DrawerFooter = styled.div`
  display: flex;
  flex-wrap: wrap;
  justify-content: flex-end;
  gap: 0.5rem;
  margin-top: ${({ theme }) => theme.spacing.md};
  padding-top: ${({ theme }) => theme.spacing.sm};
  border-top: 1px solid ${({ theme }) => theme.colours?.line || '#e8e8e8'};
`

export default FoodSafetySetupScreen
